/** @jsxRuntime classic */
/** @jsx jsx */
import { useMemo, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { css, jsx } from '@emotion/react';

import { Typography as Text } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import TextField from '@mui/material/TextField';

import { useTranscriptUploadActions } from '../../actions/FileActions';

import UploadSettings from './UploadSettings';
import { useUserQuery } from '../../actions/UserActions';

import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import DialogHeader from './DialogHeader';
import Groups2Icon from '@mui/icons-material/Groups2';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import ThumbsUpIcon from '@mui/icons-material/ThumbUp';
import Tooltip from '@mui/material/Tooltip';
import {
  IS_SINGLE_TENENT_MODE,
  CALENDAR_RECIPIENT_PREFIX,
} from '../../constants';

import { Link } from 'react-router-dom';

const googleMeetRegex = /^https:\/\/meet\.google\.com\/([\w-]+)$/;
const teamsRegex1 = /^https:\/\/teams\.microsoft\.com\/l\/meetup-join\/(.+)/;
const teamsRegex2 = /^https:\/\/teams\.live\.com\/meet\/(.+)/;
const compodiumMeetRegex = /^https:\/\/meet\.compodium\.com\/video\/(.+)/;
const zoomRegex = /^https:\/\/(.+)?\.?(zoom\.us|zoom-x\.de)\/(j|my)\/(.+)/;

function testTeamsRegex(url) {
  return teamsRegex1.test(url) || teamsRegex2.test(url);
}

function getURLObj(url) {
  try {
    return new URL(url);
  } catch (err) {
    return {};
  }
}

export default function UploadUrl({ handleClose }) {
  const { t } = useTranslation();
  const { uploadTranscriptAsync } = useTranscriptUploadActions();
  const [settings, setSettings] = useState({
    numSpeakers: 0,
    language: 'auto',
    outputFormat: 'transcript',
    transcribeMode: 0,
    name: new Date().toLocaleString('sv-SE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }),
  });

  const { user } = useUserQuery();

  const [url, setUrl] = useState('');
  const [step, setStep] = useState(0);

  const baseUrl = useMemo(() => {
    const urlObj = getURLObj(url);
    return `${urlObj.origin}${urlObj.pathname}`;
  }, [url]);

  const urlError =
    url?.length > 0 &&
    !googleMeetRegex.test(baseUrl) &&
    !testTeamsRegex(baseUrl) &&
    !compodiumMeetRegex.test(baseUrl) &&
    !zoomRegex.test(baseUrl);

  async function onUpload() {
    await uploadTranscriptAsync({
      ...settings,
      url,
    });
    setStep(2);
  }

  const email = `${CALENDAR_RECIPIENT_PREFIX}+${user?.invite_email}@klang.ai`;
  const hasSyncedCalendar = user?.calendar_types?.length > 0;
  const [showCopyNotification, setShowCopyNotification] = useState(false);

  const handleCopyEmail = () => {
    navigator.clipboard.writeText(email);
    setShowCopyNotification(true);
    setTimeout(() => setShowCopyNotification(false), 2000);
  };

  if (step === 0) {
    return (
      <div>
        <DialogHeader
          title={t('Spela in webbmöte')}
          icon={Groups2Icon}
          onClose={handleClose}
        />
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 1rem;
          `}
        >
          <div>
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: 0.5rem;
                margin-bottom: 0.5rem;
              `}
            >
              <Text variant="subtitle2" fontWeight="600">
                {t('Bjud in till kalenderinbjudan')}
              </Text>
              <Tooltip
                title={t(
                  'Vidarebefodra din kalenderinbjudan till mejl adressen för att bjuda in Klang.ai till ett framtida möte.'
                )}
              >
                <InfoOutlinedIcon
                  fontSize="small"
                  css={css`
                    color: rgba(0, 0, 0, 0.54);
                  `}
                />
              </Tooltip>
            </div>
            <div
              css={css`
                display: flex;
                align-items: center;
                justify-content: space-between;
                background-color: rgba(238, 238, 238, 1);
                padding: 8px;
                padding-left: 16px;
                border-radius: 8px;
              `}
            >
              <Text>{email}</Text>
              <IconButton
                size="small"
                onClick={handleCopyEmail}
                css={css`
                  color: #151851;
                `}
              >
                <ContentCopyIcon fontSize="small" color="inherit" />
              </IconButton>
            </div>
            {showCopyNotification && (
              <Text
                variant="caption"
                color="success.main"
                css={css`
                  margin-left: 8px;
                `}
              >
                {t('Epost kopierad!')}
              </Text>
            )}
          </div>
          <div>
            <Text
              variant="subtitle2"
              fontWeight="600"
              css={css`
                margin-bottom: 0.5rem;
              `}
            >
              {t('Möte nu?')}
            </Text>
            <div
              css={css`
                border: 1px solid rgba(0, 0, 0, 0.12);
                border-radius: 8px;
                cursor: pointer;
              `}
            >
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  gap: 0.5rem;
                  padding: 8px;
                  padding-left: 16px;
                  ${!hasSyncedCalendar &&
                  'border-bottom: 1px solid rgba(0, 0, 0, 0.12);'}
                `}
                onClick={() => setStep(1)}
              >
                <VideocamOutlinedIcon color="text.primary" />
                <Text>{t('Direktmöte')}</Text>
              </div>
              {!hasSyncedCalendar && (
                <Link
                  css={css`
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 8px;
                    padding-left: 16px;
                    text-decoration: none;
                  `}
                  to="/meetings"
                  onClick={handleClose}
                >
                  <MailOutlineIcon color="text.primary" />
                  <Text>{t('Synka din kalender')}</Text>
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (step === 2) {
    return (
      <div>
        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 1rem;
          `}
        >
          <div
            css={css`
              background-color: var(--success-4p);
              border-radius: 50%;
              width: 54px;
              height: 54px;
              display: flex;
              align-items: center;
              justify-content: center;
            `}
          >
            <ThumbsUpIcon color="info" fontSize="large" />
          </div>
          <Text>{t('Klang hoppar snart in i ditt möte')}</Text>
          <Text
            variant="subtitle2"
            color="text.secondary"
            textAlign="center"
            maxWidth="360px"
          >
            {t(
              'Klang kommer knacka på och spela in ditt mötet för transkribering.'
            )}
          </Text>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => handleClose()}
            fullWidth
          >
            {t('Stäng fönster')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <DialogHeader
        title={t('Spela in webbmöte')}
        icon={Groups2Icon}
        onClose={handleClose}
      />
      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: 1rem;
        `}
      >
        <Text
          variant="subtitle2"
          color="#00000099"
          css={css`
            font-size: 14px;
          `}
        >
          {t('Spela in möten från Google Meet, Microsoft Teams eller Zoom.')}
        </Text>
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
          `}
        >
          <Text variant="subtitle2" fontWeight="600">
            {t('Möteslänk')}
          </Text>
          <TextField
            variant="outlined"
            fullWidth
            size="small"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            error={urlError}
            helperText={
              urlError
                ? t(
                    'Länken måste vara en giltig länk till Google Meet, Microsoft Teams eller Zoom'
                  )
                : ''
            }
            placeholder="https://"
          />
        </div>
        <Button
          variant="contained"
          color="primary"
          fullWidth
          onClick={onUpload}
          disabled={
            !url?.length ||
            urlError ||
            !settings.name ||
            !settings.outputFormat ||
            settings?.folderPrefix?.split('/')?.length <= 2
          }
        >
          {t('Bjud in Klang')}
        </Button>
        <UploadSettings
          settings={settings}
          setSettings={setSettings}
          nameEdit
        />
      </div>
    </div>
  );
}
